import {
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  PaperclipIcon,
  UploadSimpleIcon,
  SpinnerIcon,
  XIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  useArCollectionThreadDocumentNames,
  useArCollectionThreadEvidence,
} from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { useUploadEntityDocument } from '@/api/rpc/hooks/core/useEntityDocuments';
import { getClassificationLabel } from '@/utils/emailClassificationUtils';
import {
  EvidenceViewerModal,
  type EvidenceViewerDocument,
} from './EvidenceViewerModal';

interface Props {
  threadId: string;
  selectedIds: string[];
  onSelectedIdsChange: Dispatch<SetStateAction<string[]>>;
  disabled?: boolean;
}

interface AttachableDoc {
  id: string;
  name: string | null;
  classificationType: string | null;
  /** null when unknown (thread evidence rows don't carry size). */
  fileSizeBytes: number | null;
}

/**
 * The string the backend recognises for documents attached in the context of
 * a collection thread. Mirrors the `AR_COLLECTION_THREAD` value of
 * `DocumentAssociationEntityType` on the backend.
 */
const COLLECTION_THREAD_ENTITY_TYPE = 'ar_collection_thread';

/**
 * Mirrors `GRAPH_INLINE_ATTACHMENT_MAX_BYTES` on the backend — the maximum
 * total size of base64 inline attachments Microsoft Graph's sendMail accepts.
 * The send handler enforces this server-side; we surface it client-side so a
 * clerk sees the problem before they spend a click on Send.
 */
const MAX_ATTACHMENT_TOTAL_BYTES = 3_000_000;

export function AttachmentsPanel({
  threadId,
  selectedIds,
  onSelectedIdsChange,
  disabled,
}: Props) {
  const gt = useGT();
  const { evidence, isLoading } = useArCollectionThreadEvidence(threadId);
  // Same LLM-generated readable names the Evidence panel shows, so an
  // attachment reads as "Signed proof of delivery" rather than a raw filename.
  const { namesByDocumentId } = useArCollectionThreadDocumentNames(threadId);
  const uploadMutation = useUploadEntityDocument();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Newly-uploaded docs aren't in the evidence cache until the next
  // background refetch — keep them locally so they appear in the picker
  // immediately and survive list re-renders within this dialog open.
  const [uploadedDocs, setUploadedDocs] = useState<AttachableDoc[]>([]);
  // The document open in the full-screen viewer (clicking an attachment name),
  // or null when closed. Paging walks the whole attachable list.
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);

  const available: AttachableDoc[] = useMemo(() => {
    const seen = new Set<string>();
    const list: AttachableDoc[] = [];
    // Show fresh uploads first so the user can see what just landed.
    for (const u of uploadedDocs) {
      if (seen.has(u.id)) continue;
      seen.add(u.id);
      list.push(u);
    }
    for (const d of evidence.documents) {
      if (seen.has(d.id)) continue;
      seen.add(d.id);
      list.push({
        id: d.id,
        name: d.originalFileName,
        classificationType: d.classificationType ?? null,
        fileSizeBytes: d.fileSizeBytes,
      });
    }
    return list;
  }, [evidence.documents, uploadedDocs]);

  // The viewer pages across every attachable doc in list order; map to the
  // minimal shape it needs (readable names come in via namesByDocumentId).
  const viewerDocuments: EvidenceViewerDocument[] = useMemo(
    () =>
      available.map((d) => ({
        id: d.id,
        originalFileName: d.name,
        classificationType: d.classificationType,
      })),
    [available],
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedCount = selectedIds.length;
  const selectedDocs = available.filter((d) => selectedSet.has(d.id));
  const selectedKnownSize = selectedDocs.reduce(
    (sum, d) => (d.fileSizeBytes != null ? sum + d.fileSizeBytes : sum),
    0,
  );
  const selectedWithUnknownSize = selectedDocs.some(
    (d) => d.fileSizeBytes == null,
  );
  const overLimit = selectedKnownSize > MAX_ATTACHMENT_TOTAL_BYTES;

  function toggle(id: string) {
    onSelectedIdsChange((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function removeSelected(id: string) {
    onSelectedIdsChange((prev) => prev.filter((x) => x !== id));
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    // Sequential uploads — keeps the toast/error story simple and matches how
    // every other entity uses `useUploadEntityDocument`.
    for (const file of Array.from(files)) {
      try {
        const result = await uploadMutation.mutateAsync({
          entityType: COLLECTION_THREAD_ENTITY_TYPE,
          entityId: threadId,
          file,
        });
        setUploadedDocs((prev) => [
          {
            id: result.documentId,
            name: result.name ?? file.name,
            classificationType: null,
            fileSizeBytes: result.fileSizeBytes ?? file.size,
          },
          ...prev,
        ]);
        onSelectedIdsChange((prev) =>
          prev.includes(result.documentId)
            ? prev
            : [...prev, result.documentId],
        );
      } catch {
        // The hook already surfaces a toast on failure; bail this batch.
        break;
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const isUploading = uploadMutation.isPending;
  const interactionDisabled = disabled || isUploading;

  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <PaperclipIcon className="h-3.5 w-3.5" />
          <T context="section title, attachments to include with the email">
            Attachments
          </T>
          {selectedCount > 0 && (
            <span
              className={cn(
                'normal-case font-normal',
                overLimit ? 'text-destructive' : 'text-foreground/80',
              )}
            >
              {selectedKnownSize > 0 ? (
                <T context="attachment count and total size in the attachments header">
                  · <Var>{selectedCount}</Var> selected (
                  <Var>{formatBytes(selectedKnownSize)}</Var>)
                </T>
              ) : (
                <T context="attachment count in the attachments header">
                  · <Var>{selectedCount}</Var> selected
                </T>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <Button
            size="sm"
            variant="outline"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={interactionDisabled}
            className="h-7 text-xs"
          >
            {isUploading ? (
              <SpinnerIcon className="h-3.5 w-3.5 mr-1 animate-spin" />
            ) : (
              <UploadSimpleIcon className="h-3.5 w-3.5 mr-1" />
            )}
            <T context="button, upload a new file to attach">Upload file</T>
          </Button>
        </div>
      </div>

      {isLoading && available.length === 0 ? (
        <div className="text-xs text-muted-foreground py-1">
          <T context="status, loading thread documents for the attachments picker">
            Loading thread documents…
          </T>
        </div>
      ) : available.length === 0 ? (
        <div className="text-xs text-muted-foreground py-1">
          <T context="empty state, no documents available for this collection thread">
            No documents on this thread yet. Upload one to attach it.
          </T>
        </div>
      ) : (
        <ul className="space-y-1">
          {available.map((doc) => {
            const checked = selectedSet.has(doc.id);
            // Prefer the LLM-generated readable name (as in the Evidence
            // panel), then the classification label, then the raw filename.
            const classificationLabel = doc.classificationType
              ? getClassificationLabel(doc.classificationType)
              : null;
            const displayName =
              namesByDocumentId.get(doc.id) ??
              classificationLabel ??
              doc.name ??
              gt('Untitled document', {
                context: 'placeholder, document with no original filename',
              });
            // Show the raw filename beside the readable name — unless the
            // readable name already is the filename. It fades out on overflow;
            // the tooltip carries both in full.
            const showFilename = doc.name != null && doc.name !== displayName;
            const rowTitle = showFilename
              ? `${displayName} · ${doc.name}`
              : displayName;
            return (
              <li
                key={doc.id}
                className="flex items-center gap-2 rounded border bg-card px-2 py-1 text-xs"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(doc.id)}
                  disabled={interactionDisabled}
                />
                <button
                  type="button"
                  onClick={() => setOpenDocumentId(doc.id)}
                  className="group flex min-w-0 flex-1 cursor-pointer items-baseline gap-1.5 text-left"
                  title={rowTitle}
                >
                  <span className="max-w-[55%] shrink-0 truncate group-hover:underline">
                    {displayName}
                  </span>
                  {showFilename && (
                    <span
                      className="min-w-0 flex-1 overflow-hidden whitespace-nowrap text-muted-foreground/60"
                      // Fade the tail rather than hard-clip / ellipsis when the
                      // filename overruns the row; full text lives in the tooltip.
                      style={{
                        maskImage:
                          'linear-gradient(to right, black 85%, transparent)',
                        WebkitMaskImage:
                          'linear-gradient(to right, black 85%, transparent)',
                      }}
                    >
                      {doc.name}
                    </span>
                  )}
                </button>
                {doc.classificationType && (
                  <Badge
                    variant="outline"
                    className="text-[10px] capitalize pointer-events-none"
                  >
                    {doc.classificationType.replace(/_/g, ' ')}
                  </Badge>
                )}
                {doc.fileSizeBytes != null && (
                  <span className="text-muted-foreground tabular-nums">
                    {formatBytes(doc.fileSizeBytes)}
                  </span>
                )}
                {checked && (
                  <button
                    type="button"
                    onClick={() => removeSelected(doc.id)}
                    disabled={interactionDisabled}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={gt('Remove attachment', {
                      context: 'a11y label, remove this attachment',
                    })}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {overLimit && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive flex items-start gap-2">
          <WarningIcon className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            <div className="font-semibold">
              <T context="warning title, total attachment size exceeds inline limit">
                Attachments exceed the size limit
              </T>
            </div>
            <div>
              <T context="warning detail, current total vs. max attachment size">
                Selected total <Var>{formatBytes(selectedKnownSize)}</Var>{' '}
                exceeds the <Var>{formatBytes(MAX_ATTACHMENT_TOTAL_BYTES)}</Var>{' '}
                inline limit. Remove an attachment or compress the files before
                sending.
              </T>
            </div>
          </div>
        </div>
      )}

      {!overLimit && selectedWithUnknownSize && selectedCount > 0 && (
        <div className="text-xs text-muted-foreground">
          <T context="hint, attachment total may be incomplete">
            One or more attachments don&apos;t report a size; the total above
            may be incomplete and the send may still hit the{' '}
            <Var>{formatBytes(MAX_ATTACHMENT_TOTAL_BYTES)}</Var> limit.
          </T>
        </div>
      )}

      <EvidenceViewerModal
        documents={viewerDocuments}
        namesByDocumentId={namesByDocumentId}
        openDocumentId={openDocumentId}
        onClose={() => setOpenDocumentId(null)}
        onNavigate={setOpenDocumentId}
      />
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
